import React, { useEffect } from 'react';

import { Controller, useFormContext } from 'react-hook-form';

import Input from 'components/Inputs/Input';
import Select from 'components/Select';
import { Checkbox, CheckboxType } from 'components/Checkbox';
import styles from 'pages/ContractorPortal/steps/ContractorApplication/ContractorApplication.module.scss';
import {
  SOMEONE_ELSE_OPTION,
  DEFAULT_REQUIRED_MESSAGE,
} from 'pages/ContractorPortal/steps/ContractorApplication/сontractorApplication.constants';
import {
  BusinessEntityStructure,
  HoldLicense,
  InputLabel,
  InstallProductsYouSell,
  MainContactPosition,
  PrimaryHomeImprovementCategory,
} from 'pages/ContractorPortal/steps/ContractorApplication/contractorApplication.enums';
import {
  convertTotalOwnersValueToNumber,
  getUniqueMasterDialerOptions,
  prepareTotalOwnersArray,
} from 'pages/ContractorPortal/steps/ContractorApplication/сontractorApplication.utils';
import { FormDataContractorApplication } from 'pages/ContractorPortal/steps/ContractorApplication/contractorApplications.types';
import { getMessageForRequiredFields } from 'utils/validationUtils';
import { AppRoutes } from 'routes/routes.enums';
import useAppNavigate from 'hooks/useAppNavigate';
import { setContractorPortalData } from 'actions';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { getStreetNameFromStreetAddress, getStreetNumberFromStreetAddress, removeNonDigits } from 'utils/stringUtils';

const MasterDialerAgreement = () => {
  const {
    register,
    unregister,
    setValue,
    control,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext();
  const watcher = watch();
  const navigator = useAppNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    register('intermediary_who_signed_master_dealer_agreement', {
      required: {
        value: !watcher.intermediary_who_signed_master_dealer_agreement,
        message: DEFAULT_REQUIRED_MESSAGE,
      },
    });

    register('intermediary_agreed_to_master_dealer_agreement', {
      required: {
        value: !watcher.intermediary_agreed_to_master_dealer_agreement,
        message: DEFAULT_REQUIRED_MESSAGE,
      },
    });
  }, [register, unregister, watcher]);

  useEffect(() => {
    if (watcher.intermediary_who_signed_master_dealer_agreement === SOMEONE_ELSE_OPTION) {
      register('intermediary_authorized_signer_first_name', {
        required: {
          value: !watcher.intermediary_authorized_signer_first_name,
          message: DEFAULT_REQUIRED_MESSAGE,
        },
      });

      register('intermediary_authorized_signer_last_name', {
        required: {
          value: !watcher.intermediary_authorized_signer_last_name,
          message: DEFAULT_REQUIRED_MESSAGE,
        },
      });

      register('intermediary_authorized_signer_position', {
        required: {
          value: !watcher.intermediary_authorized_signer_position,
          message: DEFAULT_REQUIRED_MESSAGE,
        },
      });
    } else {
      unregister('intermediary_authorized_signer_first_name');
      unregister('intermediary_authorized_signer_last_name');
      unregister('intermediary_authorized_signer_position');
    }
  }, [register, unregister, watcher.intermediary_who_signed_master_dealer_agreement]);

  const ownersArray = prepareTotalOwnersArray(watcher as FormDataContractorApplication);

  const isShowMasterDialerAgreement =
    watcher.intermediary_main_contact_first_name &&
    watcher.intermediary_main_contact_last_name &&
    watcher.intermediary_main_contact_email &&
    watcher.intermediary_main_contact_phone &&
    watcher.intermediary_main_contact_position &&
    watcher.intermediary_total_owners &&
    ownersArray.every((owner) => owner.firstName && owner.lastName);
  const options = getUniqueMasterDialerOptions(
    ownersArray,
    watcher.intermediary_main_contact_first_name,
    watcher.intermediary_main_contact_last_name,
  );

  const isShowCheckboxMasterDialerAgreement =
    isShowMasterDialerAgreement &&
    watcher.intermediary_federal_tax_id &&
    watcher.intermediary_business_entity_structure &&
    watcher.intermediary_business_name &&
    watcher.intermediary_corporate_address &&
    watcher.intermediary_phone &&
    watcher.intermediary_email &&
    watcher.intermediary_who_signed_master_dealer_agreement &&
    (watcher.intermediary_who_signed_master_dealer_agreement === SOMEONE_ELSE_OPTION
      ? watcher.intermediary_authorized_signer_first_name &&
        watcher.intermediary_authorized_signer_last_name &&
        watcher.intermediary_authorized_signer_position
      : true);

  const navigateToMasterDealerAgreement = () => {
    dispatch(
      setContractorPortalData({
        contractorApplication: {
          intermediary_corporate_entity_name: watcher.intermediary_corporate_entity_name,
          intermediary_business_name: watcher.intermediary_business_name,
          intermediary_federal_tax_id: watcher.intermediary_federal_tax_id.replace(/\D/g, ''),
          intermediary_business_entity_structure:
            watcher.intermediary_business_entity_structure as BusinessEntityStructure,
          intermediary_year_of_incorporation: watcher.intermediary_year_of_incorporation,
          intermediary_phone: watcher.intermediary_phone,
          intermediary_email: watcher.intermediary_email?.toLowerCase(),
          intermediary_corporate_address_information: {
            streetNumber: getStreetNumberFromStreetAddress(watcher.intermediary_corporate_address),
            streetName: getStreetNameFromStreetAddress(watcher.intermediary_corporate_address),
            city: watcher.intermediary_corporate_city,
            stateOrProvince: watcher.intermediary_corporate_state,
            zipOrPostalCode: watcher.intermediary_corporate_zip_code,
          },
          intermediary_mailing_address_information: {
            streetNumber: getStreetNumberFromStreetAddress(watcher.intermediary_mailing_address),
            streetName: getStreetNameFromStreetAddress(watcher.intermediary_mailing_address),
            city: watcher.intermediary_city,
            stateOrProvince: watcher.intermediary_state,
            zipOrPostalCode: watcher.intermediary_zip_code,
          },
          intermediary_website_or_facebook_page: watcher.intermediary_website_or_facebook_page,
          intermediary_states_of_operation: watcher.intermediary_states_of_operation,
          intermediary_do_you_install_the_products_you_sell:
            watcher.intermediary_do_you_install_the_products_you_sell === InstallProductsYouSell.Yes,
          intermediary_installer_count: +watcher.intermediary_installer_count,
          intermediary_associated_manufacturer_1: watcher.intermediary_associated_manufacturer_1,
          intermediary_associated_manufacturer_2: watcher.intermediary_associated_manufacturer_2,
          intermediary_annual_sales: +removeNonDigits(watcher.intermediary_annual_sales),
          intermediary_average_jobs: +watcher.intermediary_average_jobs,
          intermediary_years_in_business: +watcher.intermediary_years_in_business,
          intermediary_hold_appropriate_license: watcher.intermediary_hold_appropriate_license === HoldLicense.Yes,
          intermediary_hold_appropriate_license_description:
            watcher.intermediary_hold_appropriate_license_description || null,
          intermediary_main_contact_first_name: watcher.intermediary_main_contact_first_name,
          intermediary_main_contact_last_name: watcher.intermediary_main_contact_last_name,
          intermediary_main_contact_phone: watcher.intermediary_main_contact_phone,
          intermediary_main_contact_email: watcher.intermediary_main_contact_email?.toLowerCase(),
          intermediary_primary_home_improvement_category:
            watcher.intermediary_primary_home_improvement_category as PrimaryHomeImprovementCategory,
          intermediary_other_home_improvement_category:
            watcher.intermediary_other_home_improvement_category as PrimaryHomeImprovementCategory[],
          intermediary_main_contact_position: watcher.intermediary_main_contact_position as MainContactPosition,
          intermediary_percent_of_sales_financed: +watcher.intermediary_percent_of_sales_financed / 100,
          intermediary_total_owners: convertTotalOwnersValueToNumber(watcher.intermediary_total_owners),
          intermediary_owners: prepareTotalOwnersArray(watcher as FormDataContractorApplication),
          intermediary_how_did_you_hear_about_us: watcher.intermediary_how_did_you_hear_about_us,
          intermediary_other_home_improvement_type: watcher.intermediary_other_home_improvement_type || '',
          intermediary_who_signed_master_dealer_agreement: watcher.intermediary_who_signed_master_dealer_agreement,
          intermediary_authorized_signer_first_name: watcher.intermediary_authorized_signer_first_name || '',
          intermediary_authorized_signer_last_name: watcher.intermediary_authorized_signer_last_name || '',
          intermediary_authorized_signer_position: watcher.intermediary_authorized_signer_position || '',
        },
      }),
    );

    navigator(AppRoutes.MasterDealerAgreement, { replace: false });
  };

  return (
    <>
      {isShowMasterDialerAgreement && (
        <div>
          <div className={styles.blockHeader}>Master Dealer Agreement</div>
          <div className={styles.form_row}>
            <Select
              label={InputLabel.WhoisSigningTheMasterDealerAgreement}
              name="intermediary_who_signed_master_dealer_agreement"
              useFormControl
              control={control}
              required
              options={options}
              value={options.find((option) => option.label === watcher.intermediary_who_signed_master_dealer_agreement)}
              errorMessage={errors?.intermediary_who_signed_master_dealer_agreement?.message}
              inputContainerClassName={styles.input}
              className={styles.dropdown}
              isClearable={!!watcher.intermediary_who_signed_master_dealer_agreement}
            />
          </div>
          {watcher.intermediary_who_signed_master_dealer_agreement === SOMEONE_ELSE_OPTION && (
            <>
              <div className={styles.form_row}>
                <Input
                  label={InputLabel.SignerFirstName}
                  name="intermediary_authorized_signer_first_name"
                  value={watcher.intermediary_authorized_signer_first_name}
                  errorMessage={errors.intermediary_authorized_signer_first_name?.message}
                  inputContainerClassName={styles.input}
                  onChange={(event) => {
                    setValue('intermediary_authorized_signer_first_name', event.target.value);
                  }}
                />
                <Input
                  label={InputLabel.SignerLastName}
                  name="intermediary_authorized_signer_last_name"
                  value={watcher.intermediary_authorized_signer_last_name}
                  errorMessage={errors.intermediary_authorized_signer_last_name?.message}
                  inputContainerClassName={styles.input}
                  onChange={(event) => {
                    setValue('intermediary_authorized_signer_last_name', event.target.value);
                  }}
                />
              </div>
              <div className={styles.form_row}>
                <Input
                  label={InputLabel.SignerPosition}
                  name="intermediary_authorized_signer_position"
                  value={watcher.intermediary_authorized_signer_position}
                  errorMessage={errors.intermediary_authorized_signer_position?.message}
                  inputContainerClassName={styles.input}
                  onChange={(event) => {
                    setValue('intermediary_authorized_signer_position', event.target.value);
                  }}
                />
              </div>
            </>
          )}
          {isShowCheckboxMasterDialerAgreement && (
            <Controller
              name="intermediary_agreed_to_master_dealer_agreement"
              control={control}
              rules={{
                required: getMessageForRequiredFields(),
                validate: (value) => value === true || getMessageForRequiredFields(),
              }}
              render={() => (
                <div className={styles.checkboxContainer}>
                  <div className={styles.checkboxWrapper}>
                    <Checkbox
                      type={CheckboxType.Alternative}
                      name="intermediary_agreed_to_master_dealer_agreement"
                      isChecked={!!watcher.intermediary_agreed_to_master_dealer_agreement}
                      onChange={() => {
                        setValue(
                          'intermediary_agreed_to_master_dealer_agreement',
                          !watcher.intermediary_agreed_to_master_dealer_agreement,
                        );
                        trigger('intermediary_agreed_to_master_dealer_agreement');
                      }}
                      className={styles.checkboxText}
                      isValid={!errors?.intermediary_agreed_to_master_dealer_agreement?.message}
                    />
                    <div className={styles.checkboxTextContainer}>
                      I/We agree to the{' '}
                      <p className={styles.checkboxTextAsLink} onClick={navigateToMasterDealerAgreement}>
                        Master Dealer Agreement
                      </p>
                    </div>
                  </div>
                  <p className={styles.errorMessage}>
                    {errors?.intermediary_agreed_to_master_dealer_agreement?.message}
                  </p>
                </div>
              )}
            />
          )}
        </div>
      )}
    </>
  );
};

export default MasterDialerAgreement;
